/** Validación de carga Contalink y preflight antes de confronta. */

export const EXCEL_UPLOAD_INTENTS = [
  {
    value: "ingreso",
    label: "Ingresos (ventas / cobros)",
    hint: "Exporte la hoja o reporte de Ingresos desde Contalink (encabezados de cobro, cuentas 401).",
    confrontaLabel: "Confronta Ingresos",
    confrontaPath: "/confronta-cfdi",
  },
  {
    value: "egreso",
    label: "Egresos (gastos / pagos)",
    hint: "Exporte la hoja o reporte de Egresos (encabezados de pago, cuentas 601/701). No borra nóminas al recargar.",
    confrontaLabel: "Confronta Egresos",
    confrontaPath: "/confronta-cfdi",
  },
  {
    value: "nomina",
    label: "Nóminas",
    hint: "Reporte o hoja de Nóminas Contalink / plantilla. Se guarda aparte de gastos para no duplicar ni borrar egresos.",
    confrontaLabel: "Confronta Nóminas",
    confrontaPath: "/confronta-cfdi",
  },
  {
    value: "mixto_3h",
    label: "Libro completo (plantilla 3 hojas)",
    hint: "Un .xlsx con pestañas Ingresos, Egresos y Nóminas (plantilla del sistema).",
    confrontaLabel: "Confronta (por pestaña)",
    confrontaPath: "/confronta-cfdi",
  },
];

export function getConfrontaFlujo(direccion) {
  return direccion === "egreso" ? "egreso" : "ingreso";
}

export function intentByValue(value) {
  return EXCEL_UPLOAD_INTENTS.find((i) => i.value === value) || EXCEL_UPLOAD_INTENTS[0];
}

/** Tras POST /excel/upload: ¿coincide lo guardado con lo que el usuario indicó? */
export function evaluateUploadResult(intent, data) {
  const fileDir = String(data?.import_file_direccion || data?.direccion || "").toLowerCase();
  const resIng = data?.carga_resumen?.ingreso;
  const resEgr = data?.carga_resumen?.egreso;
  const bdIng = data?.carga_resumen_bd?.ingreso;
  const bdEgr = data?.carga_resumen_bd?.egreso;
  const ingRows = Number(resIng?.filas ?? data?.import_preview?.ingreso_rows ?? 0);
  const egrRows = Number(resEgr?.filas ?? data?.import_preview?.egreso_rows ?? 0);
  const bdIngRows = Number(bdIng?.filas ?? 0);
  const bdEgrRows = Number(bdEgr?.filas ?? 0);
  const ingTotal = Number(resIng?.totales?.total ?? 0);
  const egrTotal = Number(resEgr?.totales?.total ?? 0);
  const issues = [];
  let intentAlreadyInBd = false;

  if (intent === "ingreso") {
    if (fileDir === "egreso" && ingRows === 0) {
      if (egrRows > 0 || Number(data?.total ?? 0) > 0) {
        // Auto-enrutado: el archivo era gastos/nóminas y se guardó en su lote.
      } else if (bdIngRows > 0) {
        intentAlreadyInBd = true;
        const archRows = Number(data?.total ?? egrRows ?? 0);
        issues.push({
          code: "INGRESO_YA_EN_BD",
          severity: "info",
          message:
            `Este archivo agregó ${archRows} fila(s) de egreso. Ya cuenta con ${bdIngRows} fila(s) de ingreso en el año`
            + (data?.carga_resumen_bd?.year ? ` ${data.carga_resumen_bd.year}` : "")
            + " — puede usar Confronta Ingresos con la información existente.",
        });
      } else {
        issues.push({
          code: "WRONG_SHEET_INGRESO",
          severity: "block",
          message:
            "El archivo se guardó como egresos y no trae filas de ingreso. Para Confronta Ingresos suba la pestaña Ingresos o la plantilla de 3 hojas.",
        });
      }
    } else if (ingTotal < 0.01 && ingRows > 0) {
      issues.push({
        code: "INGRESO_SIN_MONTOS",
        severity: "warn",
        message: "Hay filas de ingreso pero los importes suman $0. Revise que no sea la hoja Resumen.",
      });
    } else if (ingRows === 0 && egrRows > 0) {
      // Contenido egreso con intent ingreso: ya guardado OK; sin aviso bloqueante.
    }
  }

  if (intent === "egreso") {
    if (fileDir === "ingreso" && egrRows === 0 && ingRows > 0) {
      if (Number(data?.total ?? 0) > 0) {
        // Auto-enrutado a ingresos.
      } else if (bdEgrRows > 0) {
        intentAlreadyInBd = true;
        const archRows = Number(data?.total ?? ingRows ?? 0);
        issues.push({
          code: "EGRESO_YA_EN_BD",
          severity: "info",
          message:
            `Este archivo agregó ${archRows} fila(s) de ingreso. Ya cuenta con ${bdEgrRows} fila(s) de egreso en el año`
            + (data?.carga_resumen_bd?.year ? ` ${data.carga_resumen_bd.year}` : "")
            + " — puede usar Confronta Egresos con la información existente.",
        });
      } else {
        issues.push({
          code: "WRONG_SHEET_EGRESO",
          severity: "block",
          message:
            "El archivo se guardó como ingresos. Para Confronta Egresos suba la hoja Egresos o la plantilla de 3 hojas.",
        });
      }
    } else if (egrTotal < 0.01 && egrRows > 0) {
      issues.push({
        code: "EGRESO_SIN_MONTOS",
        severity: "warn",
        message: "Hay filas de egreso pero los importes suman $0.",
      });
    }
  }

  if (intent === "nomina") {
    const resNom = data?.carga_resumen?.nomina;
    const nomRows = Number(resNom?.filas ?? data?.import_preview?.nomina_rows ?? 0);
    if (nomRows === 0 && (ingRows > 0 || egrRows > 0)) {
      issues.push({
        code: "WRONG_SHEET_NOMINA",
        severity: "block",
        message:
          "No se detectaron filas de nómina. Elija Ingresos o Egresos según el archivo, o use la hoja Nóminas / plantilla 3 hojas.",
      });
    }
  }

  if (intent === "mixto_3h" && ingRows === 0 && egrRows === 0) {
    issues.push({
      code: "MIXTO_VACIO",
      severity: "block",
      message:
        "No se reconocieron hojas Ingresos/Egresos/Nóminas. Use la plantilla de 3 hojas con esos nombres de pestaña.",
    });
  }

  const severity = intentAlreadyInBd
    ? "ok"
    : issues.some((i) => i.severity === "block")
      ? "block"
      : issues.some((i) => i.severity === "warn")
        ? "warn"
        : "ok";

  return {
    severity,
    issues,
    fileDir,
    ingRows,
    egrRows,
    ingTotal,
    egrTotal,
    bdIngRows,
    bdEgrRows,
    intentAlreadyInBd,
    resumenIngresoDisplay: intentAlreadyInBd && intent === "ingreso" ? bdIng : resIng,
    resumenEgresoDisplay: intentAlreadyInBd && intent === "egreso" ? bdEgr : resEgr,
  };
}

const CONFRONTA_FLUJO_UI = {
  ingreso: {
    section: "Confronta Ingresos",
    open: "ingresos",
    cargaOpcion: "Ingresos (ventas / cobros)",
    cuentaHint: "cuentas 401",
  },
  egreso: {
    section: "Confronta Egresos",
    open: "egresos",
    cargaOpcion: "Egresos (gastos / pagos)",
    cuentaHint: "cuentas 601/701",
  },
  nomina: {
    section: "Confronta Nóminas",
    open: "nominas",
    cargaOpcion: "Nóminas (plantilla 3 hojas o hoja Nominas)",
    cuentaHint: "hoja Nóminas",
  },
};

function fmtMxn(n) {
  return Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

/** Meses con fechas contables en BD, según el flujo de la sección abierta. */
function mesesCobroHint(diagnostico, year, flujo = "ingreso") {
  const byFlujo =
    flujo === "egreso"
      ? diagnostico?.por_mes_flujo_egreso
      : flujo === "nomina"
        ? diagnostico?.por_mes_flujo_nomina
        : diagnostico?.por_mes_flujo_ingreso;
  // Preferir mapa por flujo; el legado por_mes_fecha_ingreso mezcla gastos y cobros.
  const mesesFlujo = sortedMesKeys(byFlujo);
  if (mesesFlujo.length) {
    const lista = mesesFlujo.slice(0, 8);
    const tail = mesesFlujo.length > lista.length ? "…" : "";
    const label =
      flujo === "egreso"
        ? "fechas de egreso/pago"
        : flujo === "nomina"
          ? "fechas de nómina"
          : "cobros de ingreso";
    return ` En BD hay ${label} en: ${lista.join(", ")}${tail}.`;
  }
  const mesesAll = sortedMesKeys(diagnostico?.por_mes_fecha_ingreso);
  if (!mesesAll.length) return "";
  const yr = year ? String(year) : "";
  const fuera = yr ? mesesAll.filter((m) => !m.startsWith(yr)) : mesesAll;
  const lista = (fuera.length ? fuera : mesesAll).slice(0, 8);
  if (!lista.length) return "";
  const tail = mesesAll.length > lista.length ? "…" : "";
  return ` En BD hay fechas contables en: ${lista.join(", ")}${tail}. Ajuste el año o el rango de fechas.`;
}

/** Año sugerido cuando hay cobros en BD fuera del año seleccionado en confronta. */
export function suggestedYearFromDiagnostico(diagnostico, year) {
  const meses = sortedMesKeys(diagnostico?.por_mes_fecha_ingreso);
  if (!meses.length) return null;
  const years = [...new Set(
    meses.map((m) => parseInt(String(m).slice(0, 4), 10)).filter((y) => y > 2000),
  )].sort((a, b) => a - b);
  if (!years.length) return null;
  const cur = Number(year) || 0;
  if (cur && years.includes(cur)) return null;
  return years[years.length - 1];
}

function preflightWarn(base) {
  return { ...base, ok: false, level: "warn", canRunAnyway: true };
}

/** Preflight confronta: solo datos del flujo de la sección abierta (ingreso / egreso / nómina). */
export function evaluateConfrontaPreflight({ flujo, diagnostico, excelResumen, year, fechaDesde, fechaHasta }) {
  const ui = CONFRONTA_FLUJO_UI[flujo] || CONFRONTA_FLUJO_UI.ingreso;
  const conteo = diagnostico?.conteo_flujos || {};
  const filasRango = Number(diagnostico?.filas_en_rango ?? excelResumen?.filas ?? 0);
  const filasFlujo = Number(diagnostico?.filas_flujo ?? excelResumen?.filas ?? 0);
  const totFlujo = Number(
    diagnostico?.totales_flujo?.total
      ?? diagnostico?.totales_flujo?.neto
      ?? excelResumen?.totales?.total
      ?? excelResumen?.totales?.neto
      ?? 0,
  );
  const ing = Number(conteo.ingreso ?? 0);
  const egr = Number(conteo.egreso ?? 0);
  const nom = Number(conteo.nomina ?? 0);
  const totIng = Number(conteo.totales_ingreso ?? 0);
  const totEgr = Number(conteo.totales_egreso ?? 0);
  const totNom = Number(conteo.totales_nomina ?? 0);
  const pref401 = Number(conteo.etiqueta_pref_401 ?? 0);
  const pref601 = Number(conteo.etiqueta_pref_601 ?? 0);
  const mesHint = mesesCobroHint(diagnostico, year, flujo);
  const suggestedYear = suggestedYearFromDiagnostico(diagnostico, year);
  const yearFrom = parseInt(String(fechaDesde || "").slice(0, 4), 10);
  const yearTo = parseInt(String(fechaHasta || "").slice(0, 4), 10);
  const yearMismatch =
    year
    && yearFrom
    && yearTo
    && (yearFrom !== year || yearTo !== year)
    && filasRango === 0;

  const baseOk = (filas, total, labelFlujo) => ({
    ok: true,
    level: "ok",
    title: `${ui.section}: datos listos`,
    message:
      `${filas} fila(s) de ${labelFlujo} en el periodo`
      + (total > 0.01 ? ` · total ${fmtMxn(total)}` : "")
      + ".",
    filasRango,
    totFlujo: total,
    filasFlujo: filas,
    showReclasificar: true,
  });

  if (filasRango === 0) {
    return preflightWarn({
      title: "Sin contabilidad en el periodo",
      message: yearMismatch
        ? `No hay filas Excel entre ${fechaDesde} y ${fechaHasta}. El año seleccionado es ${year}: cargue el Excel de ${ui.cargaOpcion} con ese año o ajuste el periodo.${mesHint}`
        : `No hay filas de Excel en BD para ${fechaDesde} → ${fechaHasta}. Cargue ${ui.cargaOpcion} en Carga de información (mismo cliente).${mesHint}`,
      filasRango,
      totFlujo: 0,
      suggestedYear,
      showReclasificar: false,
    });
  }

  if (flujo === "ingreso" && filasFlujo > 0) {
    return baseOk(filasFlujo, totFlujo > 0.01 ? totFlujo : totIng, "ingreso");
  }
  if (flujo === "egreso" && filasFlujo > 0) {
    return baseOk(filasFlujo, totFlujo > 0.01 ? totFlujo : totEgr, "egreso");
  }
  if (flujo === "nomina" && filasFlujo > 0) {
    return baseOk(filasFlujo, totFlujo > 0.01 ? totFlujo : totNom, "nómina");
  }

  if (flujo === "ingreso") {
    if (egr > 0 && ing === 0) {
      const mesesEgr = sortedMesKeys(diagnostico?.por_mes_flujo_egreso);
      const egrMes =
        mesesEgr.length
          ? ` Hay ${egr} fila(s) de egreso/gasto en: ${mesesEgr.slice(0, 6).join(", ")}${mesesEgr.length > 6 ? "…" : ""}.`
          : ` Hay ${egr} fila(s) de egreso/gasto en el periodo.`;
      return preflightWarn({
        title: "Falta Excel de Ingresos",
        message:
          `Confronta Ingresos necesita el reporte Contalink de Ingresos (cuentas 401).`
          + egrMes
          + ` Los CFDI del SAT no sustituyen ese Excel. Cargue Ingresos en Carga de información,`
          + ` o use Confronta Egresos si solo quiere cruzar gastos.`,
        filasRango,
        totFlujo: 0,
        suggestedOpen: "egresos",
        suggestedYear,
        wrongFlujoOnly: true,
        showReclasificar: false,
      });
    }
    if (pref401 > 0 && ing === 0) {
      return preflightWarn({
        title: "Sin ingresos en este periodo",
        message:
          `Hay ${pref401} fila(s) con cuenta 401 en Etiqueta en el periodo, pero ninguna entra a ${ui.section}. `
          + `Revise la Fecha de ingreso (cobro) o use Re-clasificar flujo.${mesHint}`,
        filasRango,
        totFlujo: 0,
        suggestedOpen: null,
        suggestedYear,
        showReclasificar: true,
      });
    }
    return preflightWarn({
      title: "Sin ingresos en este periodo",
      message:
        `Ninguna fila del periodo está en ${ui.section} (${ui.cuentaHint}). `
        + (pref601 > 0 && pref401 === 0
          ? `Use ${CONFRONTA_FLUJO_UI.egreso.section} para gastos del periodo. `
          : `Cargue ${ui.cargaOpcion} en Carga de información.`)
        + mesHint,
      filasRango,
      totFlujo: 0,
      suggestedOpen: pref601 > 0 && pref401 === 0 ? "egresos" : null,
      suggestedYear,
      showReclasificar: true,
    });
  }

  if (flujo === "egreso") {
    if (ing > 0 && egr === 0) {
      return preflightWarn({
        title: "Sin egresos en este periodo",
        message:
          `${ui.section} no tiene filas de egreso (${ui.cuentaHint}) entre ${fechaDesde} y ${fechaHasta}. `
          + `Cargue ${ui.cargaOpcion} en Carga de información o use ${CONFRONTA_FLUJO_UI.ingreso.section} para ventas/cobros.${mesHint}`,
        filasRango,
        totFlujo: 0,
        suggestedOpen: "ingresos",
        suggestedYear,
        wrongFlujoOnly: true,
        showReclasificar: false,
      });
    }
    if (pref601 > 0 && egr === 0) {
      return preflightWarn({
        title: "Sin egresos en este periodo",
        message:
          `Hay ${pref601} fila(s) con cuenta 601 en Etiqueta en el periodo, pero ninguna entra a ${ui.section}. `
          + `Revise la Fecha de egreso (pago) o use Re-clasificar flujo.${mesHint}`,
        filasRango,
        totFlujo: 0,
        suggestedOpen: null,
        suggestedYear,
        showReclasificar: true,
      });
    }
    return preflightWarn({
      title: "Sin egresos en este periodo",
      message:
        `Ninguna fila del periodo está en ${ui.section} (${ui.cuentaHint}). `
        + (pref401 > 0 && pref601 === 0
          ? `Use ${CONFRONTA_FLUJO_UI.ingreso.section} para ingresos del periodo. `
          : `Cargue ${ui.cargaOpcion} en Carga de información.`)
        + mesHint,
      filasRango,
      totFlujo: 0,
      suggestedOpen: pref401 > 0 && pref601 === 0 ? "ingresos" : null,
      suggestedYear,
      showReclasificar: true,
    });
  }

  if (flujo === "nomina") {
    if ((ing > 0 || egr > 0) && nom === 0) {
      return preflightWarn({
        title: "Sin nóminas en este periodo",
        message:
          `${ui.section} no tiene filas de nómina en ${fechaDesde} → ${fechaHasta}. `
          + `Cargue ${ui.cargaOpcion} o abra la pestaña de ingresos/egresos según corresponda.${mesHint}`,
        filasRango,
        totFlujo: 0,
        suggestedOpen: ing >= egr ? "ingresos" : "egresos",
        suggestedYear,
        wrongFlujoOnly: true,
        showReclasificar: false,
      });
    }
    return preflightWarn({
      title: "Sin nóminas en este periodo",
      message:
        `No hay filas de nómina en ${fechaDesde} → ${fechaHasta}. Cargue ${ui.cargaOpcion}.`,
      filasRango,
      totFlujo: 0,
      suggestedYear,
      showReclasificar: true,
    });
  }

  return baseOk(filasFlujo, totFlujo, flujo);
}

export function sortedMesKeys(porMes) {
  return Object.keys(porMes || {}).sort();
}

/** Texto corto para «última carga» en el selector de tipo de Excel. */
export function formatExcelLastUpload(iso, { filas, year } = {}) {
  if (!iso) {
    return year ? `Sin cargas en ${year}` : "Sin cargas registradas";
  }
  let when = iso.slice(0, 16).replace("T", " ");
  try {
    when = new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Mexico_City",
    }).format(new Date(iso));
  } catch {
    /* keep fallback */
  }
  const n = Number(filas ?? 0);
  if (n > 0) {
    return `Última carga: ${when} · ${n.toLocaleString("es-MX")} fila(s) en el año`;
  }
  return `Última carga: ${when}`;
}

/**
 * Mensaje de éxito tras POST /excel/upload: fecha, tipo e info cargada.
 * @param {object} data respuesta del API
 * @param {string} intent ingreso|egreso|nomina|mixto_3h
 * @param {{ loadedAt?: Date|string|null, fileName?: string }} [opts]
 */
export function formatExcelUploadSuccessMessage(data, intent, opts = {}) {
  const whenIso = opts.loadedAt
    ? (opts.loadedAt instanceof Date ? opts.loadedAt.toISOString() : String(opts.loadedAt))
    : new Date().toISOString();
  let when = whenIso.slice(0, 16).replace("T", " ");
  try {
    when = new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Mexico_City",
    }).format(new Date(whenIso));
  } catch {
    /* keep */
  }
  const opt = intentByValue(intent);
  const total = Number(data?.total ?? 0);
  const inserted = Number(data?.inserted ?? 0);
  const updated = Number(data?.updated ?? 0);
  const skipped = Number(data?.skipped ?? 0);
  const months = Array.isArray(data?.replaced_months) ? data.replaced_months.filter(Boolean) : [];
  const fileBit = opts.fileName ? ` «${opts.fileName}»` : "";
  let msg =
    `Carga exitosa el ${when}: ${opt.label}${fileBit} — `
    + `${total.toLocaleString("es-MX")} fila(s)`
    + ` (${inserted.toLocaleString("es-MX")} nuevas, ${updated.toLocaleString("es-MX")} actualizadas`;
  if (skipped > 0) {
    msg += `, ${skipped.toLocaleString("es-MX")} sin cambios`;
  }
  msg += ")";
  // La carga Contalink ya no elimina filas previas: solo corrige si hay cambios.
  const replaced = Number(data?.deleted_year_rows ?? 0);
  if (replaced > 0) {
    msg += `. Se retiraron ${replaced.toLocaleString("es-MX")} fila(s) previas`;
  } else if (updated > 0 || skipped > 0 || inserted > 0) {
    msg += ". Las filas ya cargadas de otros meses se conservan; solo se corrigen diferencias";
  }
  if (months.length) {
    msg += `. Meses: ${months.join(", ")}`;
  }
  if (data?.excel_source === "plantilla_3h") {
    msg += ". Origen: plantilla 3 hojas";
  } else if (data?.excel_source) {
    msg += ". Origen: Contalink / Control IVA";
  }
  return msg;
}
